import { normalize } from 'normalizr';

import { AppointmentTypeRaw, AppointmentTypeNormalized } from 'EntityTypes';
import { AppQueryConfig } from 'QueryTypes';
import { EntitiesById, AppointmentTypesState, GenericEntityState } from 'StoreTypes';
import { mapArrayToObject } from 'utils/arrays';
import {
  makeDeleteFns,
  makeOptimisticFns,
  makeSortingOptimisticFns,
  makeUpdateFn,
} from 'utils/redux-query';

import { appointmentTypeSchema } from './appointmentTypesSchemas';
import { pseudoNormalize } from './utils';

/**
 * Adapts a single-arg entity-state transformer (delete, optimistic update) to propagate
 * the same mutation to both byId and byIdUnfiltered independently. byIdUnfiltered is only
 * modified if it has already been populated — an unpopulated key is left as undefined so
 * that callers can distinguish "not yet fetched" from "fetched but empty".
 */
function applyToBothKeys(
  fn: (
    state: GenericEntityState<AppointmentTypeNormalized>,
  ) => GenericEntityState<AppointmentTypeNormalized>,
) {
  return (prevState: AppointmentTypesState | undefined): AppointmentTypesState => ({
    ...prevState,
    byId: fn({ byId: prevState?.byId }).byId,
    byIdUnfiltered:
      prevState?.byIdUnfiltered !== undefined
        ? fn({ byId: prevState.byIdUnfiltered }).byId
        : undefined,
  });
}

/**
 * Adapts a two-arg rollback function to restore both byId and byIdUnfiltered independently.
 * byIdUnfiltered is only restored if it was populated in currentState.
 */
function rollbackBothKeys(
  fn: (
    initial: GenericEntityState<AppointmentTypeNormalized>,
    current: GenericEntityState<AppointmentTypeNormalized>,
  ) => GenericEntityState<AppointmentTypeNormalized>,
) {
  return (
    initialState: AppointmentTypesState | undefined,
    currentState: AppointmentTypesState | undefined,
  ): AppointmentTypesState => ({
    ...(currentState ?? {}),
    byId: fn({ byId: initialState?.byId }, { byId: currentState?.byId }).byId,
    byIdUnfiltered:
      currentState?.byIdUnfiltered !== undefined
        ? fn({ byId: initialState?.byIdUnfiltered }, { byId: currentState?.byIdUnfiltered }).byId
        : undefined,
  });
}

/**
 * Adapts a two-arg update function (prevState + server response entities) to propagate
 * new entities into both byId and byIdUnfiltered independently.
 * byIdUnfiltered is only updated if it has already been populated.
 */
function updateBothKeys(
  fn: (
    prevState: GenericEntityState<AppointmentTypeNormalized>,
    newEntities: EntitiesById<AppointmentTypeNormalized>,
  ) => GenericEntityState<AppointmentTypeNormalized>,
) {
  return (
    prevState: AppointmentTypesState | undefined,
    newEntities: EntitiesById<AppointmentTypeNormalized>,
  ): AppointmentTypesState => ({
    ...prevState,
    byId: fn({ byId: prevState?.byId }, newEntities).byId,
    byIdUnfiltered:
      prevState?.byIdUnfiltered !== undefined
        ? fn({ byId: prevState.byIdUnfiltered }, newEntities).byId
        : undefined,
  });
}

const urls = {
  appointmentTypes(practiceId: number, appointmentTypeId?: number): string {
    let url = `/book/api/admin/${practiceId}/appointment_types`;
    if (appointmentTypeId) {
      url += `/${appointmentTypeId}`;
    }
    return url;
  },
  appointmentTypesLegacy(practiceId: number, appointmentTypeId: number): string {
    let url = `/practice/${practiceId}/settings/scheduler/appointment-types/`;
    if (appointmentTypeId) {
      url += `${appointmentTypeId}/`;
    }
    return url;
  },
  sortAppointmentTypes(practiceId: number): string {
    return `/practice/${practiceId}/settings/scheduler/appointment-types/sort/`;
  },
};

/**
 * Query to get/create/update a single appointment type.
 *
 * Note that this query CAN fetch deleted appointment types.
 *
 * - Specify `practiceId` and `appointmentTypeId` to fetch an appointment type
 * - Specify `practiceId` and `body` with a null appointmentTypeId to create a new appointment type
 * - Specify all parameters to update an appointment type
 */
export const appointmentTypeQuery = (
  practiceId: number,
  appointmentTypeId: number | null,
  body?: Partial<AppointmentTypeRaw>,
): AppQueryConfig => {
  const isUpdating = Boolean(appointmentTypeId && body);

  const queryConfig: AppQueryConfig = {
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'number | null' is not assignable... Remove this comment to see the full error message
    url: urls.appointmentTypes(practiceId, appointmentTypeId),
    body,
    meta: { autoCaseKeys: false },
    transform: (responseJson: {
      data: AppointmentTypeRaw;
    }): { appointmentTypes: EntitiesById<AppointmentTypeNormalized> } => {
      return normalize(responseJson.data, appointmentTypeSchema).entities;
    },
    update: {
      // @ts-expect-error updateBothKeys second arg is EntitiesById (the transform result), not the entity state type that UpdateStrategy expects
      appointmentTypes: updateBothKeys(makeUpdateFn<AppointmentTypeNormalized>()),
    },
  };

  if (isUpdating) {
    queryConfig.options = {
      method: 'PUT',
    };
    const { optimisticUpdate, rollback } = makeOptimisticFns<AppointmentTypeNormalized>(
      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'number | null' is not assignable... Remove this comment to see the full error message
      appointmentTypeId,
      pseudoNormalize(body as AppointmentTypeRaw, false),
    );
    queryConfig.optimisticUpdate = {
      appointmentTypes: applyToBothKeys(optimisticUpdate),
    };
    queryConfig.rollback = {
      appointmentTypes: rollbackBothKeys(rollback),
    };
  }

  return queryConfig;
};

/**
 * Query to fetch the list of appointment types for a practice.
 *
 * @param practiceId - The ID of the practice
 * @param ignoreWorkspace - When true, bypasses server-side workspace filtering to return all
 *                          appointment types. Use for workspace configuration screens where all
 *                          types must be visible. Data is stored in `byIdUnfiltered` to avoid
 *                          conflicts with workspace-filtered data in `byId`.
 */
export const appointmentTypesQuery = (
  practiceId: number,
  ignoreWorkspace = false,
): AppQueryConfig => {
  let url = urls.appointmentTypes(practiceId);
  if (ignoreWorkspace) {
    url += '?ignore_workspace=true';
  }
  return {
    url,
    transform: (responseJson: {
      data: AppointmentTypeRaw | AppointmentTypeRaw[];
    }): { appointmentTypes: EntitiesById<AppointmentTypeNormalized> } => {
      return normalize(responseJson.data, [appointmentTypeSchema]).entities;
    },
    update: {
      // @ts-expect-error ts-migrate(2322) FIXME: Type '(prevState: AppointmentTypesState | undefine... Remove this comment to see the full error message
      appointmentTypes(
        prevState,
        fetchedApptTypes: EntitiesById<AppointmentTypeNormalized>,
      ): AppointmentTypesState {
        // Write to a separate key so workspace-filtered scheduler queries cannot
        // overwrite the full practice list fetched by settings pages, and vice versa.
        if (ignoreWorkspace) {
          // Preserve deleted types so historical associations (e.g. patient forms linked to
          // a deleted appointment type) remain resolvable after a refetch.
          const deletedApptTypes = prevState?.byIdUnfiltered
            ? Object.values(prevState.byIdUnfiltered).filter((apptType) => apptType.delete_date)
            : [];
          return {
            ...prevState,
            byIdUnfiltered: {
              ...mapArrayToObject(deletedApptTypes, 'id'),
              ...fetchedApptTypes,
            },
          };
        }

        // Preserve deleted appointment types in byId so historical data remains
        // accessible when it's needed (e.g. past appointments).
        const deletedApptTypes = prevState?.byId
          ? Object.values(prevState.byId).filter((apptType) => apptType.delete_date)
          : [];

        return {
          ...prevState,
          byId: {
            ...mapArrayToObject(deletedApptTypes, 'id'),
            ...fetchedApptTypes,
          },
        };
      },
    },
  };
};

export const deleteAppointmentTypeQuery = (
  practiceId: number,
  appointmentTypeId: number,
): AppQueryConfig => {
  const { update, optimisticUpdate, rollback } =
    makeDeleteFns<AppointmentTypeNormalized>(appointmentTypeId);

  return {
    url: urls.appointmentTypesLegacy(practiceId, appointmentTypeId),
    options: {
      method: 'DELETE',
    },
    update: {
      appointmentTypes: applyToBothKeys(update),
    },
    optimisticUpdate: {
      appointmentTypes: applyToBothKeys(optimisticUpdate),
    },
    rollback: {
      appointmentTypes: rollbackBothKeys(rollback),
    },
  };
};

export const sortAppointmentTypesQuery = (
  practiceId: number,
  appointmentTypeIds: number[],
): AppQueryConfig => {
  const { optimisticUpdate, rollback } = makeSortingOptimisticFns<AppointmentTypeNormalized>(
    appointmentTypeIds,
    'sequence',
  );
  return {
    url: urls.sortAppointmentTypes(practiceId),
    body: {
      sequence: appointmentTypeIds,
    },
    transform: (responseJson: AppointmentTypeRaw[]): EntitiesById<AppointmentTypeNormalized> =>
      normalize(responseJson, [appointmentTypeSchema]).entities,
    update: {
      // @ts-expect-error updateBothKeys second arg is EntitiesById (the transform result), not the entity state type that UpdateStrategy expects
      appointmentTypes: updateBothKeys(makeUpdateFn()),
    },
    optimisticUpdate: {
      appointmentTypes: applyToBothKeys(optimisticUpdate),
    },
    rollback: {
      appointmentTypes: rollbackBothKeys(rollback),
    },
  };
};
